import { formatPrice } from "@/lib/format";
import { FREE_SHIPPING_EUR } from "@/lib/publicApi";

const input = "h-12 w-full rounded-xl border border-ink/20 bg-ivory px-4 text-base text-ink outline-none transition-[border-color] focus:border-bordeaux";
const ZONES = [["pt-continental", "Portugal Continental"], ["madeira", "Madeira"], ["acores", "Açores"]];

const F = ({ id, label, hint, value, onChange, testId, optional, ...rest }) => (
  <div className="space-y-1.5">
    <label htmlFor={id} className="block text-sm font-semibold text-ink">{label}{optional && <span className="font-normal text-taupe"> (opcional)</span>}</label>
    <input id={id} data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className={input} {...rest} />
    {hint && <p className="text-xs text-taupe">{hint}</p>}
  </div>
);

const Section = ({ title, children }) => (
  <fieldset className="min-w-0 space-y-5">
    <legend className="mb-5 font-serif text-2xl text-ink">{title}</legend>
    {children}
  </fieldset>
);

export const ContactFields = ({ value, onChange }) => {
  const set = (k) => (v) => onChange({ ...value, [k]: v });
  return (
    <Section title="Contacto">
      <F id="c-name" label="Nome completo" testId="checkout-name" autoComplete="name" value={value.name} onChange={set("name")} required maxLength={100} />
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <F id="c-email" label="Email" testId="checkout-email" type="email" autoComplete="email" value={value.email} onChange={set("email")} required hint="Para a confirmação da encomenda." />
        <F id="c-phone" label="Telefone" testId="checkout-phone" type="tel" autoComplete="tel" value={value.phone} onChange={set("phone")} required minLength={9} maxLength={20} hint="Útil para a transportadora." />
      </div>
      <F id="c-nif" label="NIF para fatura" optional testId="checkout-nif" inputMode="numeric" value={value.nif} onChange={set("nif")} maxLength={20} />
    </Section>
  );
};

export const AddressFields = ({ value, onChange, zone, onZone }) => {
  const set = (k) => (v) => onChange({ ...value, [k]: v });
  return (
    <Section title="Morada de envio">
      <div className="space-y-1.5">
        <label htmlFor="c-zone" className="block text-sm font-semibold text-ink">Destino</label>
        <select id="c-zone" data-testid="checkout-zone" value={zone} onChange={(e) => onZone(e.target.value)} className={input}>
          {ZONES.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
      </div>
      <p className="text-xs text-taupe">Só fazemos envios para Portugal.</p>
      <F id="c-a1" label="Morada" testId="checkout-address1" autoComplete="address-line1" value={value.address_line1} onChange={set("address_line1")} required maxLength={120} />
      <F id="c-a2" label="Andar, porta, outros" optional testId="checkout-address2" autoComplete="address-line2" value={value.address_line2} onChange={set("address_line2")} maxLength={120} />
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <F id="c-pc" label="Código postal" testId="checkout-postal" autoComplete="postal-code" placeholder="1234-567" value={value.postal_code} onChange={set("postal_code")} required maxLength={12} />
        <F id="c-city" label="Localidade" testId="checkout-city" autoComplete="address-level2" value={value.city} onChange={set("city")} required maxLength={60} />
      </div>
      <div className="space-y-1.5">
        <label htmlFor="c-notes" className="block text-sm font-semibold text-ink">Notas ou pedidos especiais <span className="font-normal text-taupe">(opcional)</span></label>
        <textarea id="c-notes" data-testid="checkout-notes" rows={3} maxLength={500} value={value.notes} onChange={(e) => set("notes")(e.target.value)} className={`${input} h-auto py-3`} />
      </div>
      <div className="space-y-1.5">
        <label htmlFor="c-baby" className="block text-sm font-semibold text-ink">Nome do bebé <span className="font-normal text-taupe">(opcional)</span></label>
        <input id="c-baby" data-testid="checkout-baby-name" value={value.baby_name} maxLength={40} onChange={(e) => set("baby_name")(e.target.value)} placeholder="Ex.: Maria" className={input} />
        <p className="text-xs text-taupe">Só para eu saber para quem é a encomenda e personalizar a embalagem. Não altera as peças.</p>
      </div>
    </Section>
  );
};

export const ShippingOptions = ({ methods, value, onChange, failed = false, onRetry, free = false, missing = 0 }) => (
  <Section title="Portes">
    {free && <p className="rounded-xl bg-rose-soft px-4 py-3 text-sm font-medium text-bordeaux" data-testid="checkout-free-shipping">Portes grátis nesta encomenda.</p>}
    {!free && missing > 0 && <p className="text-sm text-taupe-strong" data-testid="checkout-free-shipping-hint">Faltam {formatPrice(missing)} para teres portes grátis (a partir de {formatPrice(FREE_SHIPPING_EUR)}).</p>}
    {methods === null && (
      <div role="status" aria-live="polite" data-testid="checkout-shipping-loading">
        <p className="flex items-center gap-2.5 text-sm text-taupe"><span className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-bordeaux/25 border-t-bordeaux" aria-hidden="true" />A calcular opções de envio...</p>
        <div className="mt-3 space-y-3" aria-hidden="true">{[0, 1].map((k) => <div key={k} className="h-14 animate-pulse rounded-2xl bg-linen" />)}</div>
      </div>
    )}
    {failed && (
      <div className="rounded-xl bg-rose-soft px-4 py-3 text-sm text-bordeaux" role="alert" data-testid="checkout-shipping-error">
        Não consegui calcular os portes. <button type="button" onClick={onRetry} className="font-semibold underline underline-offset-4">Tentar novamente</button>
      </div>
    )}
    {!failed && methods?.length === 0 && <p className="rounded-xl bg-rose-soft px-4 py-3 text-sm text-bordeaux" data-testid="checkout-no-shipping">Não há opções de envio para este destino e peso. Contacta-me.</p>}
    <div className="space-y-3" role="radiogroup" aria-label="Opções de portes">
      {methods?.map((m) => (
        <label key={m.id} data-testid={`checkout-shipping-option-${m.id}`} className={`flex min-h-[56px] cursor-pointer items-center justify-between gap-4 rounded-2xl border px-4 py-3 transition-colors ${value === m.id ? "border-bordeaux bg-rose-soft" : "border-ink/15 hover:border-bordeaux/40"}`}>
          <span className="flex items-center gap-3">
            <input type="radio" name="shipping" checked={value === m.id} onChange={() => onChange(m.id)} className="h-4 w-4 accent-[rgb(var(--bb-bordeaux))]" />
            <span><span className="block text-sm font-semibold text-ink">{m.name}</span>{m.description && <span className="block text-xs text-taupe-strong">{m.description}</span>}</span>
          </span>
          <span className="text-sm font-semibold">{free ? "Grátis" : formatPrice(m.price)}</span>
        </label>
      ))}
    </div>
  </Section>
);
